import React, { useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import s from './Menu.scss';
import Grid from '../Grid';
import Link from '../Link';
import List from '../List';
import { sortBy } from '../../helpers';
import { getProductIdFromUrl } from '../../helpers/url';
import { triggerSuperGiftCardClickEvent } from '../../helpers/googleTagManager';
import Label from '../Label/Label';

const itemsToColumns = (items, columnLimit) => {
  const result = [];
  const columns = Math.ceil(items.length / columnLimit);
  for (let i = 0; i < columns; i += 1) {
    result.push(items.slice(i * columnLimit, i * columnLimit + columnLimit));
  }
  return result;
};

const Menu = props => {
  const {
    children,
    items,
    className,
    itemsClassName,
    position,
    salesChannelId,
    mode,
    currency,
    name,
    linkTo,
    onClick,
    ...rest
  } = props;
  const [isOpen, setIsOpen] = useState(false);

  const openMenu = () => setIsOpen(true);

  const closeMenu = () => setIsOpen(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  const gtmSuperGiftCardClickEvent = (_localId, to, isSuperCardBuy, label) => {
    closeMenu();
    if (isSuperCardBuy) {
      const productId = getProductIdFromUrl(to);
      triggerSuperGiftCardClickEvent({
        eventData: {
          currencyCode: currency,
          click: {
            actionField: {
              list: 'header',
              salesChannelId,
              mode,
            },
            products: [
              {
                id: productId,
                name: label,
              },
            ],
          },
        },
      });
    }
  };

  const hasSubMenu = items?.length > 0;
  const instanceId = name.replaceAll(' ', '_').toLowerCase();

  const handleKeyBoardNavigation = e => {
    const currentMenu = e.target.closest(`#menu-${instanceId}`);
    const insideSubMenu =
      hasSubMenu && Boolean(e.target.closest(`#submenu-${instanceId}`));
    const currentGridColumn = e.target.closest('.submenu-grid-column');
    const previousLink = currentGridColumn?.previousSibling?.querySelector('a');
    const nextLink = currentGridColumn?.nextSibling?.querySelector('a');
    switch (e.keyCode) {
      case 13: // ENTER
      case 32: // SPACE
        toggleMenu();
        break;
      case 27: // ESCAPE
        closeMenu();
        e.target.blur();
        break;
      case 40: // DOWN
        if (insideSubMenu) {
          e.target.parentNode?.nextSibling?.querySelector('a')?.focus(); // focus on next link
        } else {
          currentMenu
            ?.querySelector(`#submenu-${instanceId}`) // focus on first submenu child
            ?.querySelector('a')
            .focus();
        }
        break;
      case 38: // UP
        if (insideSubMenu) {
          e.target.parentNode?.previousSibling?.querySelector('a')?.focus();
        } else {
          closeMenu();
        }
        break;
      case 37: // LEFT
        if (insideSubMenu && previousLink) {
          previousLink.focus(); // swicth between grid columns
        } else {
          currentMenu?.previousSibling?.querySelector('a').focus();
        }
        break;
      case 39: // RIGHT
        if (insideSubMenu && nextLink) {
          nextLink.focus();
        } else {
          currentMenu?.nextSibling?.querySelector('a')?.focus();
        }
        break;
      default:
        break;
    }
  };
  const sortedItems = items?.sort(sortBy('label')) || null;
  const indexOfSeeAllItem = sortedItems?.findIndex(item => item?.isSeeAll);

  if (hasSubMenu && indexOfSeeAllItem !== -1) {
    const seeAllItem = sortedItems.splice(indexOfSeeAllItem, 1)[0];
    sortedItems.push(seeAllItem);
  }

  const columns = sortedItems ? itemsToColumns(sortedItems, 18) : null;
  return (
    <List
      item
      component="li"
      id={`menu-${instanceId}`}
      onMouseOver={openMenu}
      onMouseLeave={closeMenu}
      onFocus={openMenu}
      onBlur={closeMenu}
      onKeyDown={handleKeyBoardNavigation}
      onClick={toggleMenu}
      className={cx(s.root, className)}
      role="menuitem"
      aria-haspopup={hasSubMenu ? 'menu' : 'false'}
      aria-expanded={isOpen ? 'true' : 'false'}
      {...rest}
    >
      <Link to={linkTo || ''} onClick={() => onClick?.()}>
        <Label bold tag="h1">
          {name}
        </Label>
      </Link>
      {hasSubMenu && (
        <div
          id={`submenu-${instanceId}`}
          className={cx(
            s.submenu,
            isOpen && s['submenu--open'],
            position && s[`submenu--pos-${position}`],
          )}
        >
          <Grid container spacing={32} wrap="nowrap">
            {columns?.map(column => (
              <Grid item key={column[0].id} className="submenu-grid-column">
                <List
                  container
                  direction="column"
                  wrap="nowrap"
                  spacing={8}
                  role="menu"
                >
                  {column?.map(
                    ({ id, label, to, isSeeAll, isSuperCardBuy }) =>
                      to && (
                        <List key={id} item component="li" role="menuitem">
                          <Link
                            to={to}
                            className={cx(
                              s.item,
                              itemsClassName,
                              isSeeAll && s.seeAllItem,
                            )}
                            onClick={() => {
                              gtmSuperGiftCardClickEvent(
                                id,
                                to,
                                isSuperCardBuy,
                                label,
                              );
                            }}
                          >
                            {label}
                          </Link>
                        </List>
                      ),
                  )}
                </List>
              </Grid>
            ))}
          </Grid>
        </div>
      )}
    </List>
  );
};

Menu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string.isRequired,
  itemsClassName: PropTypes.string,
  position: PropTypes.string,
  mode: PropTypes.string.isRequired,
  salesChannelId: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  linkTo: PropTypes.string,
};

Menu.defaultProps = {
  itemsClassName: undefined,
  position: undefined,
  linkTo: undefined,
  onClick: null,
};

export default withStyles(s)(Menu);
